import type { DataLayerSearchClick, DataLayerSearchCriteria } from '@/entities/analytics';
import type { HotelProduct } from '@/entities/hotel';
import { HOTEL_CATEGORIES } from '@/entities/hotelCategory';
import type { Product } from '@/entities/product';
import { buildSearchCriteriaParams, type SearchCriteria } from '@/entities/searchCriteria';
import { filterProducts } from './filterProducts';
import { sortSerpProducts } from './sortSerpProducts';
import { toSerpProducts } from './toSerpProducts';
import { SERP_DATA_LAYER_FILTER_GROUPS } from '../config/dataLayer';
import type { FiltersModel, SerpFilterInteraction, SerpProduct, SerpSort } from '../model/types';

export interface SerpFilterDataLayer {
  sideBar: string | null;
  checked: boolean;
  value: string;
}

interface SerpDataLayerResults {
  hotels: readonly HotelProduct[];
  productsByHotel: ReadonlyMap<number, readonly Product[]>;
}

export function getSerpFilterDataLayer({ groupId, key, selected }: SerpFilterInteraction): SerpFilterDataLayer | null {
  const group = SERP_DATA_LAYER_FILTER_GROUPS[groupId];

  if (!group) return null;

  const isAny = key === null || (group.radio && !selected);
  const option = isAny ? group.any : group.options[key];

  if (!option) return null;

  return { sideBar: option.sideBar ?? null, checked: isAny || group.radio || selected, value: option.value };
}

export function getSerpTopHotelIds(
  results: SerpDataLayerResults,
  filtersModel: FiltersModel,
  sort: SerpSort,
): number[] {
  const serpProducts = toSerpProducts(results.hotels, results.productsByHotel);

  return sortSerpProducts(filterProducts(serpProducts, filtersModel), sort).map(({ hotel }) => hotel.id);
}

export function getSerpSearchClick(serpProduct: SerpProduct, position: number): DataLayerSearchClick | null {
  const { hotel, cheapProduct } = serpProduct;

  if (!cheapProduct || position < 1) return null;

  return { hotelIndex: position, hotelId: hotel.id, price: cheapProduct.price, oilTax: cheapProduct.oilTax };
}

function getHotelCategoryStars(hotelCategoryId: number): number {
  return HOTEL_CATEGORIES.find(({ id }) => id === hotelCategoryId)?.stars ?? 0;
}

export function getSerpDataLayerCriteria(criteria: SearchCriteria): DataLayerSearchCriteria {
  const { departureCity, countries, touristGroup, nightRange, checkInDateRange } = buildSearchCriteriaParams(criteria);

  return {
    cityFrom: departureCity.id,
    countryIds: countries,
    hotelStars: (criteria.hotelCategories ?? []).map((id) => getHotelCategoryStars(id)),
    touristGroup: { ...touristGroup, touristsByRooms: touristGroup.touristsByRooms ?? [] },
    nights: { from: nightRange.from, to: nightRange.to },
    dates: {
      from: checkInDateRange.from,
      to: checkInDateRange.to,
      daysFlexible: criteria.dateRange?.daysFlexible ?? 0,
    },
  };
}
